// ═══════════════════════════════════════════════════════════════════════════════
// Invite code landing — /invite-code/:code → register (employer prefill) or home
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  INVITATION_CODE_LENGTH,
  type InvitationCodeValidateResult,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../lib/api';
import { getRedirectAfterAuth } from '../../lib/authRedirect';
import { writePendingInviteCode } from '../../lib/inviteCodeSession';
import { AuthLayout } from './AuthLayout';

const normalizeRouteCode = (raw: string | undefined): string =>
  (raw ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, INVITATION_CODE_LENGTH);

export const InviteCodeLandingPage = () => {
  const { code: routeCode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const [statusText, setStatusText] = useState(t('common.loading'));

  const code = normalizeRouteCode(routeCode);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isAuthenticated && user) {
      navigate(getRedirectAfterAuth(user), { replace: true });
      return;
    }

    const run = async () => {
      if (code.length !== INVITATION_CODE_LENGTH) {
        writePendingInviteCode({ code: '', locked: false, showInvalidHint: true });
        navigate('/register', { replace: true });
        return;
      }

      try {
        const response = await api.post<InvitationCodeValidateResult>(
          '/auth/invitation-code/validate',
          { code },
        );
        if (response.success && response.data?.valid === true) {
          writePendingInviteCode({ code, locked: true, showInvalidHint: false });
        } else {
          writePendingInviteCode({ code: '', locked: false, showInvalidHint: true });
        }
      } catch {
        writePendingInviteCode({ code: '', locked: false, showInvalidHint: true });
      }
      navigate('/register', { replace: true });
    };

    setStatusText(t('common.loading'));
    void run();
  }, [authLoading, code, isAuthenticated, navigate, t, user]);

  return (
    <AuthLayout>
      <p className="text-center text-sm text-gray-600 dark:text-gray-300" role="status">
        {statusText}
      </p>
    </AuthLayout>
  );
};
